import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { Chart, ChartModule } from 'angular-highcharts';


@Component({
  selector: 'test-multi-line-chart',
  providers: [],
  styleUrls: ['./test-multi-line-chart.scss'],
  standalone: true,
  imports: [
    ChartModule
  ],
  templateUrl: './test-multi-line-chart.html'
})
export class TestMultiLineChartComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @Input()
    public unit = '';

    @Input()
    public title = '';

    @Input()
    public value = 0;

    @Input()
    public data: any[] = [];

    @Output()
    public pointClickEvent = new EventEmitter();

    public chart: Chart = new Chart({});

    @Input()
    public subject: Subject<any>|null = null;

    @Input()
    public currentSerieCategory: number|null = null;

    @Input()
    dataAsArray = true;

    @Input()
    public graphClass: string|null = null;

    @Input()
    public yMax: number|null = null;

    private options: any;
    private subscriptions: Subscription[] = [];

    constructor(
        private translator: TranslateService
    ) {
    }

    public ngOnInit() {
        if (this.subject) {
            this.subscriptions.push(
                this.subject.subscribe((event) => {
                    if (event === 'delete') {
                        this.removePlotLines();
                    }

                    if (event === 'reflow') {
                        // this.chart.ref.reflow();
                    }
                })
            );
        }
    }

    public ngOnChanges(changes:any) {
        if (changes['data'] && null !== this.data) {
            this.addSeries(this.data, this.dataAsArray);
            this.addPlotLine(this.currentSerieCategory);
        }
    }

    public ngAfterViewInit() {
        const self = this;
        const trans = function(name:any) {
            return self.translator.instant(name);
        };

        const onPointClickedEvent = function(point:any) {
            self.pointClickEvent.emit(point);
        };

        this.options = {
            chart: {
                type: 'spline',
                events: {
                    click(this:any, event: Highcharts.ChartClickEventObject): void {
                        const serie = this.series[0];
                        if (serie) {
                            const point = serie.searchPoint(event, true);
                            if (point) {
                                self.addPlotLine(point.category);
                                onPointClickedEvent(point);
                            }
                        }
                    }
                }
            },
            title: {
                text: this.title,
            },
            xAxis: {
                type: 'datetime',
                dateTimeLabelFormats: {
                    month: '%b. %y',
                    year: '%Y'
                },
                crosshair: {
                    width: 5,
                    color: '#ccc',
                    zIndex: 1
                }
            },
            yAxis: {
                title: {
                    x: -5,
                    text: this.unit
                },
                min: this.yMax !== null ? 0 : undefined,
                max: this.yMax,
                tickInterval: 1,
                labels: {
                    formatter(this:any): string {
                        return this.value;
                    }
                }
            },
            tooltip: {
                // crosshairs: false,
                shared: true,
                xDateFormat: '%A, %d %B %Y',
                useHTML: true,
                pointFormatter(this:any): string {
                    return '<strong>' + trans(this.series.name) + ' : </strong>'
                        + this.y + ' ' + self.unit + '<br />'
                    ;
                }
            },
            plotOptions: {
                spline: {
                    marker: {
                        enabled: true
                    }
                },
                series: {
                    point: {
                        events: {
                            click(event:any): any {
                                onPointClickedEvent(event.point);
                            }
                        }
                    }
                }
            },
            legend: {
                labelFormatter(this:any): string {
                    return trans(this.name);
                }
            },
            colors: [
                '#C29D4D',
                '#000000',
                '#6DB58B',
                '#3CB7C7',
                '#99DEFF',
                '#4062D5',
                '#9142DB',
            ],

            // Define the data points. All series have a dummy year
            // of 1970/71 in order to be compared on the same x axis. Note
            // that in JavaScript, months start at 0 for January, 1 for February etc.
            series: [],
            credits: {
                enabled: false
            },
            lang: {
                noData: this.translator.instant('NoData'),
            }
        };

        this.chart = new Chart(this.options);

        if (this.data?.length) {
            this.addSeries(this.data, this.dataAsArray);
        }
    }

    public ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    private addSeries(series: any, isArray = true) {
        if (!series) {
            return;
        }

        if (!isArray) {
            series = [series];
        }

        // remove all series
        this.chart.ref$.subscribe((chart: any) => {
            while (chart.series.length) {
                chart.series[0].remove(false);
            }
        });

        for (const key of Object.keys(series)) {
            this.chart.addSeries({
                name: series[key]['name'],
                data: series[key]['data'],
                type: 'spline', // ??
            }, true, true);
        }
    }

    private addPlotLine(category: any) {
        this.subscriptions.push(
            this.chart.ref$.subscribe((ref: any) => {
                ref.xAxis[0].removePlotLine('selected-day');
                ref.xAxis[0].addPlotLine({
                    id: 'selected-day',
                    width: 5,
                    color: '#ccc',
                    dashStyle: 'solid',
                    zIndex: 1,
                    value: category,
                }
                );
            })
        );
    }

    private removePlotLines() {
        this.subscriptions.push(
            this.chart.ref$.subscribe((ref: any) => {
                ref.xAxis[0].removePlotLine('selected-day');
            })
        );
    }
}
